/**
 * Theme Management and CSS Conflict Detection Utilities
 * Provides TypeScript functions for theme switching and CSS variable validation
 */

// Theme-related interfaces
interface UserPreferences {
  reducedMotion: boolean;
  highContrast: boolean;
  colorScheme: 'light' | 'dark';
}

interface ThemeChangeEventDetail {
  theme: string;
  preferences: UserPreferences;
}

interface MediaQueries {
  prefersColorScheme: MediaQueryList;
  prefersReducedMotion: MediaQueryList;
  prefersHighContrast: MediaQueryList;
}

interface CSSVariableResult {
  available: boolean;
  value: string | null;
}

interface VariableCheckResult {
  results: Record<string, CSSVariableResult>;
  missing: string[];
  allAvailable: boolean;
}

interface PerformanceMetrics {
  loadTime: number;
  slowLoadTime: boolean;
}

interface ConflictDetectionReport {
  timestamp: string;
  userAgent: string;
  cssVariableSupport: boolean;
  variableCheck: VariableCheckResult;
  performance: PerformanceMetrics;
  recommendations: string[];
}

type Theme = 'auto' | 'light' | 'dark' | 'high-contrast';

/**
 * Theme Manager Class
 * Handles theme switching, preference detection, and storage
 */
class ThemeManager {
  private themes: Theme[] = ['auto', 'light', 'dark', 'high-contrast'];
  private currentTheme: Theme;
  private mediaQueries: MediaQueries;

  constructor() {
    this.currentTheme = this.getStoredTheme() || 'auto';
    this.mediaQueries = {
      prefersColorScheme: window.matchMedia('(prefers-color-scheme: dark)'),
      prefersReducedMotion: window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ),
      prefersHighContrast: window.matchMedia('(prefers-contrast: high)'),
    };

    this.init();
  }

  /**
   * Initialize theme manager
   */
  private init(): void {
    this.applyTheme(this.currentTheme);
    this.setupMediaQueryListeners();
    this.detectCSSVariableSupport();
  }

  /**
   * Get stored theme from localStorage
   */
  private getStoredTheme(): Theme | null {
    try {
      const stored = localStorage.getItem('theme');
      return this.themes.includes(stored as Theme) ? (stored as Theme) : null;
    } catch (e) {
      console.warn('localStorage not available for theme storage');
      return null;
    }
  }

  /**
   * Store theme in localStorage
   */
  private setStoredTheme(theme: Theme): void {
    try {
      localStorage.setItem('theme', theme);
    } catch (e) {
      console.warn('localStorage not available for theme storage');
    }
  }

  /**
   * Apply theme to document
   */
  private applyTheme(theme: Theme): void {
    const root = document.documentElement;

    // Remove existing theme attributes
    this.themes.forEach((t) => {
      root.removeAttribute(`data-theme-${t}`);
    });

    // Apply new theme
    if (theme === 'auto') {
      root.removeAttribute('data-theme');
      // Let CSS media queries handle auto theme
    } else {
      root.setAttribute('data-theme', theme);
    }

    // Update preferences
    this.updatePreferences();

    // Dispatch theme change event
    this.dispatchThemeChangeEvent(theme);

    this.currentTheme = theme;
    this.setStoredTheme(theme);
  }

  /**
   * Update accessibility preferences
   */
  private updatePreferences(): void {
    const root = document.documentElement;

    // Update motion preference
    const prefersReducedMotion = this.mediaQueries.prefersReducedMotion.matches;
    root.style.setProperty(
      '--prefers-reduced-motion',
      prefersReducedMotion ? '1' : '0'
    );

    // Update contrast preference
    const prefersHighContrast = this.mediaQueries.prefersHighContrast.matches;
    root.style.setProperty(
      '--prefers-high-contrast',
      prefersHighContrast ? '1' : '0'
    );

    // Add CSS classes for easier targeting
    root.classList.toggle('reduce-motion', prefersReducedMotion);
    root.classList.toggle('high-contrast', prefersHighContrast);
  }

  /**
   * Setup media query listeners
   */
  private setupMediaQueryListeners(): void {
    Object.values(this.mediaQueries).forEach((mq) => {
      mq.addEventListener('change', () => {
        this.updatePreferences();
        if (this.currentTheme === 'auto') {
          this.dispatchThemeChangeEvent('auto');
        }
      });
    });
  }

  /**
   * Dispatch theme change event
   */
  private dispatchThemeChangeEvent(theme: string): void {
    const event = new CustomEvent<ThemeChangeEventDetail>('themechange', {
      detail: {
        theme,
        preferences: {
          reducedMotion: this.mediaQueries.prefersReducedMotion.matches,
          highContrast: this.mediaQueries.prefersHighContrast.matches,
          colorScheme: this.mediaQueries.prefersColorScheme.matches
            ? 'dark'
            : 'light',
        },
      },
    });
    document.dispatchEvent(event);
  }

  /**
   * Switch to next theme
   */
  nextTheme(): void {
    const currentIndex = this.themes.indexOf(this.currentTheme);
    const nextIndex = (currentIndex + 1) % this.themes.length;
    this.setTheme(this.themes[nextIndex]);
  }

  /**
   * Set specific theme
   */
  setTheme(theme: Theme): void {
    if (this.themes.includes(theme)) {
      this.applyTheme(theme);
    } else {
      console.warn(`Invalid theme: ${theme}. Available themes:`, this.themes);
    }
  }

  /**
   * Get current theme
   */
  getTheme(): Theme {
    return this.currentTheme;
  }

  /**
   * Get effective theme (resolves 'auto' to actual theme)
   */
  getEffectiveTheme(): 'light' | 'dark' | 'high-contrast' {
    if (this.currentTheme === 'auto') {
      return this.mediaQueries.prefersColorScheme.matches ? 'dark' : 'light';
    }
    return this.currentTheme === 'high-contrast' ? 'high-contrast' : this.currentTheme;
  }

  /**
   * Detect CSS variable support
   */
  private detectCSSVariableSupport(): boolean {
    // Check if CSS object and supports method exist
    const hasCSS =
      typeof CSS !== 'undefined' && CSS && typeof CSS.supports === 'function';
    const supportsCustomProperties = hasCSS
      ? CSS.supports('color', 'var(--test-css-property)')
      : false;

    document.documentElement.classList.toggle(
      'no-css-variables',
      !supportsCustomProperties
    );

    if (!supportsCustomProperties) {
      console.warn(
        'CSS custom properties not supported. Fallback styles will be used.'
      );
    }

    return supportsCustomProperties;
  }
}

/**
 * CSS Conflict Detection Utilities
 */
class CSSConflictDetector {
  private static readonly TEST_COLOR = 'rgb(255, 0, 0)'; // Red color for testing
  private requiredVariables: string[] = [
    '--color-primary-500',
    '--color-background',
    '--color-text-primary',
    '--color-interactive-primary',
    '--color-surface',
    '--color-border',
  ];
  private testElement: HTMLElement | null = null;

  /**
   * Create test element for CSS variable detection
   */
  private createTestElement(): HTMLElement {
    if (this.testElement) return this.testElement;

    this.testElement = document.createElement('div');
    this.testElement.className = 'css-variable-test';
    this.testElement.style.cssText = `
      position: absolute;
      top: -9999px;
      left: -9999px;
      width: 1px;
      height: 1px;
      opacity: 0;
      pointer-events: none;
      z-index: -1;
    `;
    document.body.appendChild(this.testElement);
    return this.testElement;
  }

  /**
   * Test if a CSS variable is available
   */
  private testCSSVariable(variable: string): boolean {
    const testEl = this.createTestElement();
    const testValue = CSSConflictDetector.TEST_COLOR;

    testEl.style.color = `var(${variable}, ${testValue})`;
    const computedStyle = window.getComputedStyle(testEl);
    const computedColor = computedStyle.color;

    // If the computed color is red, the variable is missing
    return computedColor !== testValue;
  }

  /**
   * Get the computed value of a CSS variable
   */
  private getCSSVariableValue(variable: string): string {
    const testEl = this.createTestElement();
    testEl.style.color = `var(${variable})`;
    const computedStyle = window.getComputedStyle(testEl);
    return computedStyle.color;
  }

  /**
   * Check all required CSS variables
   */
  checkRequiredVariables(): VariableCheckResult {
    const results: Record<string, CSSVariableResult> = {};
    const missing: string[] = [];

    this.requiredVariables.forEach((variable) => {
      const isAvailable = this.testCSSVariable(variable);
      results[variable] = {
        available: isAvailable,
        value: isAvailable ? this.getCSSVariableValue(variable) : null,
      };

      if (!isAvailable) {
        missing.push(variable);
      }
    });

    return {
      results,
      missing,
      allAvailable: missing.length === 0,
    };
  }

  /**
   * Run comprehensive CSS conflict detection
   */
  runConflictDetection(): ConflictDetectionReport {
    const hasCSS =
      typeof CSS !== 'undefined' && CSS && typeof CSS.supports === 'function';

    const report: ConflictDetectionReport = {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      cssVariableSupport: hasCSS ? CSS.supports('color', 'var(--test)') : false,
      variableCheck: this.checkRequiredVariables(),
      performance: this.measureCSSPerformance(),
      recommendations: [],
    };

    // Add recommendations based on findings
    if (!report.cssVariableSupport) {
      report.recommendations.push(
        'Browser does not support CSS custom properties. Consider using a polyfill.'
      );
    }

    if (!report.variableCheck.allAvailable) {
      report.recommendations.push(
        'Some required CSS variables are missing. Check that all stylesheets are loaded.'
      );
    }

    if (report.performance.slowLoadTime) {
      report.recommendations.push(
        'CSS loading is slow. Consider optimizing stylesheet delivery.'
      );
    }

    return report;
  }

  /**
   * Measure CSS loading performance
   */
  private measureCSSPerformance(): PerformanceMetrics {
    const startTime = performance.now();

    // Test CSS variable resolution time
    const testEl = this.createTestElement();
    testEl.style.color = 'var(--color-primary-500, red)';
    window.getComputedStyle(testEl).color;

    const endTime = performance.now();
    const loadTime = endTime - startTime;

    return {
      loadTime,
      slowLoadTime: loadTime > 10, // Consider > 10ms as slow
    };
  }

  /**
   * Clean up test elements
   */
  cleanup(): void {
    if (this.testElement && this.testElement.parentNode) {
      this.testElement.parentNode.removeChild(this.testElement);
      this.testElement = null;
    }
  }
}

/**
 * Global CSS System Manager
 * Combines theme management and conflict detection
 */
class GlobalCSSSystem {
  private themeManager: ThemeManager;
  private conflictDetector: CSSConflictDetector;
  private initialized: boolean = false;

  constructor() {
    this.themeManager = new ThemeManager();
    this.conflictDetector = new CSSConflictDetector();
  }

  /**
   * Initialize the global CSS system
   */
  async init(): Promise<void> {
    if (this.initialized) return;

    // Run initial conflict detection
    const report = this.conflictDetector.runConflictDetection();

    if (!report.variableCheck.allAvailable) {
      console.warn(
        'CSS System Warning: Missing required variables:',
        report.variableCheck.missing
      );
    }

    // Log system status
    console.info('Global CSS System initialized:', {
      theme: this.themeManager.getTheme(),
      effectiveTheme: this.themeManager.getEffectiveTheme(),
      cssVariableSupport: report.cssVariableSupport,
      allVariablesAvailable: report.variableCheck.allAvailable,
    });

    this.initialized = true;

    // Dispatch initialization event
    document.dispatchEvent(
      new CustomEvent('globalcsssystem:ready', {
        detail: { report },
      })
    );
  }

  /**
   * Get theme manager instance
   */
  getThemeManager(): ThemeManager {
    return this.themeManager;
  }

  /**
   * Get conflict detector instance
   */
  getConflictDetector(): CSSConflictDetector {
    return this.conflictDetector;
  }

  /**
   * Run diagnostics
   */
  runDiagnostics(): ConflictDetectionReport {
    return this.conflictDetector.runConflictDetection();
  }

  /**
   * Clean up resources
   */
  cleanup(): void {
    this.conflictDetector.cleanup();
  }
}

// Create global instance
const globalCSSSystem = new GlobalCSSSystem();

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => globalCSSSystem.init());
} else {
  globalCSSSystem.init();
}

// Export for use in other modules
export { ThemeManager, CSSConflictDetector, GlobalCSSSystem, globalCSSSystem };
export type { Theme, UserPreferences, ConflictDetectionReport, VariableCheckResult };

// Also expose on window for global access
declare global {
  interface Window {
    GlobalCSSSystem: {
      ThemeManager: typeof ThemeManager;
      CSSConflictDetector: typeof CSSConflictDetector;
      GlobalCSSSystem: typeof GlobalCSSSystem;
      instance: GlobalCSSSystem;
    };
  }
}

if (typeof window !== 'undefined') {
  window.GlobalCSSSystem = {
    ThemeManager,
    CSSConflictDetector,
    GlobalCSSSystem,
    instance: globalCSSSystem,
  };
}